import React, { useRef, useState } from 'react';
import './PerguntaDoFinn.css';
import { PortaDoFinn, declararObjetivo } from '../services/api';
import { formatBRL } from '../utils/format';
import { useBotaoOcupado } from '../utils/botaoOcupado';

/**
 * A pergunta do Finn — dois toques para descobrir o que ela quer do dinheiro.
 *
 * ── Por que mora na bolha, e não num modal (08/10) ───────────────────────
 *
 * Era um modal que abria sozinho, sem ×. Decisão dela: quem fechava o
 * tutorial já levava este na cara, e vários modais em fila é ruim. A pergunta
 * passou para dentro da bolha "Perguntar", como fala do Finn: o ponto na
 * bolha avisa que ele tem algo a dizer, e ela responde quando abrir.
 *
 * Por isso este componente não tem véu, não trava a rolagem e não prende o
 * foco: quem faz a moldura (avatar, nome, balão) é o `AdvisorChat`.
 *
 * ── As duas saídas continuam sendo duas ──────────────────────────────────
 *
 *   • "é depois"      → não disse nada. O Finn volta a perguntar em trinta dias.
 *   • "ainda não sei" → DISSE. Vira declaração gravada, e o Finn passa a
 *                       perguntar de outro jeito em vez de repetir esta.
 *
 * E têm o mesmo peso visual das opções: encolhidas para texto cinza, a
 * pergunta deixaria de ser pergunta e viraria empurrão.
 *
 * ── Por que a segunda etapa não é campo em branco ────────────────────────
 *
 * "Quanto de reserva?" ela não sabe. O app sabe: `alvoSugerido` vem calculado
 * do servidor a partir das contas fixas dela. Ela confirma ou ajusta — dois
 * toques, zero digitação.
 */

/* `formatBRL` e não um formatador próprio: a regra da casa é que só
   `utils/format.ts` escreve dinheiro. `minimumFractionDigits: 2` é o padrão
   de BRL no `Intl`, então passá-lo era redundante — sai junto. */
const brl = (n: number) => formatBRL(n);

interface Props {
  portas: PortaDoFinn[];
  nome?: string;
  /** Declarou. Quem chama recarrega o que depende de meta. */
  onDeclarou: () => void;
  /** "É depois" — não declarou nada, e o Finn volta a perguntar em trinta dias. */
  onDepois: () => void;
}

const PerguntaDoFinn: React.FC<Props> = ({ portas, nome, onDeclarou, onDepois }) => {
  const [escolhida, setEscolhida] = useState<PortaDoFinn | null>(null);
  const [valor, setValor] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  // Uma opção, "Ainda não sei" ou "É isso": quem foi tocado se enche.
  const caixa = useRef<HTMLDivElement>(null);
  useBotaoOcupado(caixa, salvando);

  const primeiroNome = nome?.split(' ')[0] ?? '';

  /*
   * "Ainda não sei" sai da grade e vira saída, porque é isso que ela é: uma
   * resposta que encerra a conversa. Deixá-la entre as outras faria parecer
   * uma sexta opção de objetivo, que é o oposto do que significa.
   */
  const opcoes = portas.filter(p => p.id !== 'naosei');
  const naoSei = portas.find(p => p.id === 'naosei');

  const gravar = async (porta: PortaDoFinn, alvo: number | null) => {
    setSalvando(true);
    setErro(null);
    try {
      await declararObjetivo(porta.id, alvo);
      onDeclarou();
    } catch {
      setErro('Não consegui guardar agora. Tenta de novo?');
      setSalvando(false);
    }
  };

  const escolher = (p: PortaDoFinn) => {
    if (p.tipoDeAlvo === 'nenhum') return gravar(p, null);
    setEscolhida(p);
    setValor(p.alvoSugerido !== null ? String(p.alvoSugerido) : '');
  };

  const confirmarAlvo = () => {
    const n = Number(valor.replace(',', '.'));
    if (!Number.isFinite(n) || n <= 0) {
      setErro('Preciso de um valor maior que zero.');
      return;
    }
    if (escolhida) gravar(escolhida, n);
  };

  return (
    <div className="fm" ref={caixa}>
        {!escolhida ? (
          <>
            <p className="fm-titulo">
              {primeiroNome ? `Oi, ${primeiroNome}.` : 'Oi.'} O que você quer que
              o seu dinheiro faça por você?
            </p>
            <p className="fm-sub">
              Guardo isso para te lembrar depois, não para cobrar.
            </p>

            <div className="fm-opcoes">
              {opcoes.map(p => (
                <button
                  key={p.id}
                  className="fm-opcao"
                  disabled={salvando}
                  onClick={() => escolher(p)}
                >
                  {p.rotulo}
                </button>
              ))}
            </div>

            {/*
              As duas saídas, com o MESMO peso das opções acima. Se um dia
              alguém encolher isto para texto cinza no rodapé, o modal vira a
              armadilha que a decisão de não ter × existe para evitar.
            */}
            <div className="fm-saidas">
              <button className="fm-saida" disabled={salvando} onClick={onDepois}>
                É depois
              </button>
              {naoSei && (
                <button
                  className="fm-saida"
                  disabled={salvando}
                  onClick={() => gravar(naoSei, null)}
                >
                  Ainda não sei
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="fm-titulo">
              {escolhida.alvoSugerido !== null
                ? 'Comecei com uma conta sua.'
                : 'Quanto você precisa juntar?'}
            </p>
            <p className="fm-sub">
              {escolhida.alvoSugerido !== null
                ? `Três meses das suas contas fixas dão ${brl(escolhida.alvoSugerido)}. Dá para mudar depois.`
                : 'Um número aproximado já serve, dá para ajustar quando quiser.'}
            </p>

            <label className="fm-rotulo" htmlFor="fm-valor">Meu alvo</label>
            <input
              id="fm-valor"
              className="fm-valor"
              type="text"
              inputMode="decimal"
              value={valor}
              disabled={salvando}
              onChange={e => { setValor(e.target.value); setErro(null); }}
              onKeyDown={e => { if (e.key === 'Enter') confirmarAlvo(); }}
            />

            <div className="fm-saidas">
              <button
                className="fm-saida"
                disabled={salvando}
                onClick={() => { setEscolhida(null); setErro(null); }}
              >
                Voltar
              </button>
              <button className="fm-confirmar" disabled={salvando} onClick={confirmarAlvo}>
                É isso
              </button>
            </div>
          </>
        )}

        {erro && <p className="fm-erro" role="alert">{erro}</p>}
    </div>
  );
};

export default PerguntaDoFinn;
